import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, ShoppingCart, Check, ArrowLeft } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import Seo from "@/components/Seo";
import { useStore } from "@/context/StoreContext";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useStore();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null);
    api.get(`/products/${slug}`)
      .then(({ data }) => setProduct(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="pt-40 pb-40 text-center">
        <h1 className="font-display text-4xl font-black uppercase">Product not found</h1>
        <Link to="/shop" className="text-[#E31E24] mt-6 inline-block uppercase text-sm tracking-widest">← Back to shop</Link>
      </div>
    );
  }

  if (!product) {
    return <div className="pt-40 pb-40 grid place-items-center"><div className="w-10 h-10 border-2 border-[#E31E24] border-t-transparent rounded-full animate-spin" /></div>;
  }

  const off = product.mrp ? Math.round(((product.mrp - product.price) / product.mrp) * 100) : 0;

  return (
    <div className="pt-28 pb-16 max-w-[1600px] mx-auto px-6 md:px-10">
      <Seo
        title={`${product.name} — Indoforce`}
        description={product.short_description || product.description}
        image={product.images?.[0]}
        canonical={`${window.location.origin}/product/${product.slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name,
          image: product.images,
          description: product.description,
          brand: { "@type": "Brand", name: product.brand },
          sku: product.sku,
          offers: {
            "@type": "Offer",
            priceCurrency: "INR",
            price: product.price,
            availability: product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            url: `${window.location.origin}/product/${product.slug}`,
          },
        }}
      />

      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/50 hover:text-[#E31E24] text-xs uppercase tracking-widest mb-8" data-testid="back-btn">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="lg:sticky lg:top-28 self-start">
          <div className="relative aspect-square border border-white/10 bg-[#161616] overflow-hidden">
            <img src={product.images?.[0]} alt={product.name} className="w-full h-full object-cover" data-testid="product-image" />
            {off > 0 && <span className="absolute top-4 left-4 bg-[#E31E24] text-black text-xs font-bold px-3 py-1.5 uppercase tracking-wider">-{off}% Off</span>}
          </div>
        </motion.div>

        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#E31E24] mb-3">{product.brand} · {product.category.replace(/-/g, " ")}</p>
          <h1 className="font-display text-3xl md:text-5xl font-black uppercase tracking-tighter leading-none mb-6" data-testid="product-title">{product.name}</h1>
          <p className="text-white/60 leading-relaxed mb-8">{product.short_description}</p>

          <div className="flex items-end gap-4 mb-8 pb-8 border-b border-white/10">
            <span className="font-display text-4xl font-black" data-testid="product-price">{formatINR(product.price)}</span>
            {product.mrp && <span className="text-white/40 line-through text-lg mb-1">{formatINR(product.mrp)}</span>}
            <span className={`mb-1 text-xs uppercase tracking-widest ${product.stock > 0 ? "text-green-400" : "text-[#E63946]"}`}>
              {product.stock > 0 ? `In stock (${product.stock})` : "Out of stock"}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-4 mb-10">
            <div className="flex items-center border border-white/20">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-4 hover:text-[#E31E24]" data-testid="qty-minus"><Minus size={16} /></button>
              <span className="w-12 text-center font-display font-bold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="p-4 hover:text-[#E31E24]" data-testid="qty-plus"><Plus size={16} /></button>
            </div>
            <button
              disabled={product.stock <= 0}
              onClick={() => addToCart(product, qty)}
              data-testid="add-to-cart-btn"
              className="btn-fill flex-1 min-w-[200px] flex items-center justify-center gap-3 border border-[#E31E24] text-[#E31E24] hover:text-black px-8 py-4 uppercase text-sm font-bold tracking-widest disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ShoppingCart size={18} /> Add to cart
            </button>
          </div>

          {product.specs && Object.keys(product.specs).length > 0 && (
            <div className="mb-10">
              <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-4">Specifications</h3>
              <div className="border border-white/10">
                {Object.entries(product.specs).map(([k, v], i) => (
                  <div key={k} className={`flex justify-between px-5 py-3 text-sm ${i % 2 ? "bg-white/[0.02]" : ""}`}>
                    <span className="text-white/50 uppercase tracking-wider text-xs">{k}</span>
                    <span className="font-medium">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-4">Description</h3>
            <p className="text-white/60 leading-relaxed">{product.description}</p>
          </div>

          <div className="mt-8 flex items-center gap-2 text-white/50 text-sm">
            <Check size={16} className="text-[#E31E24]" /> Genuine product · Warranty backed · Trade support
          </div>
        </div>
      </div>
    </div>
  );
}
